<script setup lang="ts">
import { useTheme } from 'vuetify'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { getHorizontalBarChartConfig } from '@core/libs/chartjs/chartjsConfig'
import { useChartColors } from '@/utils/chartColors'
import BarChart from '@core/libs/chartjs/components/BarChart'
import { deleteWidget } from '@/modules/dashboard/composables/deleteWidget'
import { ERouteNames } from '@/router/ERouteNames'

import type { IBarChartWidget } from '@/modules/dashboard/types/IBarChartWidget'

interface Props {
  widgetData: IBarChartWidget
  currentTarget?: string
}

const props = defineProps<Props>()
const route = useRoute()
const isMonitoringRoute = computed(() => route.name === ERouteNames.MONITORING)
const vuetifyTheme = useTheme()
const showMenu = ref(false)
const { generateColors } = useChartColors()

const chartOptions = computed(() => getHorizontalBarChartConfig(vuetifyTheme.current.value))

const data = computed(() => {
  const originalData = props.widgetData.data
  const colors = generateColors(originalData.datasets.length)

  return {
    labels: originalData.labels,
    datasets: originalData.datasets.map((dataset, index) => ({
      maxBarThickness: 15,
      label: dataset.label,
      backgroundColor: colors[index],
      borderColor: 'transparent',
      borderRadius: { topRight: 15, bottomRight: 15 },
      data: dataset.data,
    })),
  }
})
</script>

<template>
  <VCard class="pa-6">
    <div class="d-flex align-center justify-space-between">
      <div class="text-h5 d-flex align-center gap-2">
        <div v-show="widgetData.show_icon && widgetData.icon" class="chart-icon-wrapper" :style="{ background: widgetData.icon_background }">
          <VIcon :icon="widgetData.icon" size="sm" />
        </div>
        {{ widgetData.name }}
      </div>
      <VMenu
        v-if="isMonitoringRoute && !currentTarget"
        v-model="showMenu"
        :close-on-content-click="false"
      >
        <template #activator="{ props: menuProps }">
          <VIconBtn
            v-bind="menuProps"
            variant="plain"
            icon="ri-more-2-line"
          />
        </template>

        <VCard>
          <VList>
            <VListItem prepend-icon="ri-delete-bin-3-line" @click="deleteWidget(props.widgetData.id)">
              <VListItemTitle>Удалить</VListItemTitle>
            </VListItem>
          </VList>
        </VCard>
      </VMenu>
    </div>
    <div class="text-body-1 mb-4">
      {{ widgetData.description }}
    </div>
    <div style="height: 375px;">
      <BarChart
        :height="375"
        :chart-data="data"
        :chart-options="chartOptions"
      />
    </div>
  </VCard>
</template>
